import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Alert, 
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Chip,
  Tooltip,
  CircularProgress as MuiCircularProgress,
  CircularProgress,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useSnackbar } from 'notistack';
import { DataGrid, GridColDef, GridSortModel, GridRowParams } from '@mui/x-data-grid';
import { 
  Search as SearchIcon, 
  Clear as ClearIcon, 
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Chat as ChatIcon,
  PhoneAndroid as MobileIcon
} from '@mui/icons-material';
import { exportToExcel, prepareClientDataForExport } from '../utils/exportToExcel';
import { getClients, updateClientStatus, getClientById, updateClient, Client } from '../services/clientService';
// Google Maps types are available globally when @types/google.maps is installed

  // Extender la interfaz Window para incluir google
  interface Window {
    google: {
      maps: {
        Map: new (mapDiv: HTMLElement, options?: google.maps.MapOptions) => google.maps.Map;
        Marker: new (options?: google.maps.MarkerOptions) => google.maps.Marker;
        Animation: {
          DROP: number;
          BOUNCE: number;
        };
        event: {
          addListener: (instance: any, eventName: string, handler: Function) => void;
          removeListener: (listener: google.maps.MapsEventListener) => void;
        };
        LatLng: new (lat: number, lng: number) => google.maps.LatLng;
        LatLngBounds: new (sw?: google.maps.LatLng | google.maps.LatLngLiteral, ne?: google.maps.LatLng | google.maps.LatLngLiteral) => google.maps.LatLngBounds;
      };
    };
  }

interface ClientRow extends Client {}

export const ClientsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [editingClient, setEditingClient] = useState<Partial<Client> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Cargar el script de Google Maps
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCh3kyJLMx6j240mmBT_9ug9UZyAac39e4`;
      script.async = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Componente para mostrar el mapa
  const MapView = React.memo(({ lat, lng }: { lat: string; lng: string }) => {
    const mapRef = React.useRef<HTMLDivElement>(null);
    const [map, setMap] = React.useState<google.maps.Map | null>(null);
    const [marker, setMarker] = React.useState<google.maps.Marker | null>(null);

    React.useEffect(() => {
      if (!mapLoaded || !mapRef.current) return;

      const position = { 
        lat: parseFloat(lat), 
        lng: parseFloat(lng) 
      };

      // Crear el mapa
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: position,
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      // Agregar marcador
      const newMarker = new window.google.maps.Marker({
        position: position,
        map: newMap,
        title: 'Ubicación del cliente',
        animation: window.google.maps.Animation.DROP
      });

      setMap(newMap);
      setMarker(newMarker);

      // Limpiar al desmontar
      return () => {
        if (marker) {
          marker.setMap(null);
        }
        setMap(null);
        setMarker(null);
      };
    }, [lat, lng, mapLoaded]);

    return (
      <div 
        ref={mapRef} 
        style={{ 
          height: '300px', 
          width: '100%', 
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          marginTop: '8px'
        }} 
      />
    );
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20, // Default to 20 rows per page
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name', sort: 'asc' },
  ]);
  
  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await getClients();
      setRows(data);
      setFilteredRows(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError('Error al cargar la lista de clientes. Por favor, inténtelo de nuevo más tarde.');
      enqueueSnackbar('Error al cargar los clientes', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredRows(rows);
    } else {
      const filtered = rows.filter(
        (row) =>
          row.name.toLowerCase().includes(searchText.toLowerCase()) ||
          row.id.toLowerCase().includes(searchText.toLowerCase()) ||
          row.channel.toLowerCase().includes(searchText.toLowerCase()) ||
          row.gec.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredRows(filtered);
    }
  }, [searchText, rows]);

  const handleStatusChange = async (id: string, newStatus: boolean) => {
    try {
      await updateClientStatus(id, newStatus);
      const updatedRows = rows.map(row => 
        row.id === id ? { ...row, enabled: newStatus } : row
      );
      setRows(updatedRows);
      enqueueSnackbar('Estado del cliente actualizado correctamente', { variant: 'success' });
    } catch (error) {
      console.error('Error updating client status:', error);
      enqueueSnackbar('Error al actualizar el estado del cliente', { variant: 'error' });
      // Revert the change in the UI if the API call fails
      fetchClients();
    }
  };

  const handleExportToExcel = () => {
    try {
      const dataToExport = prepareClientDataForExport(rows);
      exportToExcel(dataToExport, 'clientes');
      enqueueSnackbar('Exportación a Excel completada', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      enqueueSnackbar('Error al exportar a Excel', { variant: 'error' });
    }
  };

  const handleOpenDetailDialog = (client: Client) => {
    setSelectedClient(client);
    setEditingClient({ ...client });
    setIsEditing(false);
    setDetailDialogOpen(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditingClient(selectedClient ? { ...selectedClient } : null);
    setIsEditing(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editingClient) return;
    
    const { name, value } = e.target;
    setEditingClient({
      ...editingClient,
      [name]: value
    });
  };

  const handleSaveChanges = async () => {
    if (!editingClient || !selectedClient) return;
    
    try {
      setSaveLoading(true);
      const response = await updateClient(selectedClient.id, editingClient);
      
      if (response.success) {
        setSelectedClient(response.res);
        setIsEditing(false);
        
        // Actualizar la fila en la tabla
        setRows(rows.map(row => 
          row.id === selectedClient.id ? response.res : row
        ));
        
        enqueueSnackbar('Cambios guardados correctamente', { variant: 'success' });
      } else {
        throw new Error('Error al guardar los cambios');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      enqueueSnackbar('Error al guardar los cambios', { variant: 'error' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleViewDetails = async (id: string) => {
    try {
      const client = await getClientById(id);
      handleOpenDetailDialog(client);
    } catch (error) {
      console.error('Error fetching client details:', error);
      enqueueSnackbar('Error al cargar los detalles del cliente', { variant: 'error' });
    }
  };

  const renderBooleanCell = (value: boolean) => (
    <Box display="flex" justifyContent="center" width="100%">
      {value ? (
        <CheckIcon color="success" />
      ) : (
        <CloseIcon color="error" />
      )}
    </Box>
  );

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID FEMSA',
      headerAlign: 'center',
      align: 'center',
      flex: 0.8,
      minWidth: 100,
      headerClassName: 'header-bold',
    },
    { 
      field: 'name', 
      headerName: 'Nombre', 
      flex: 2,
      minWidth: 200,
    },
    { 
      field: 'channel', 
      headerName: 'Canal', 
      flex: 1.2,
      minWidth: 120,
    },
    { 
      field: 'gec', 
      headerName: 'GEC', 
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'has_mobile',
      headerName: 'Móvil',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: 'has_phone',
      headerName: 'Teléfono',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: 'has_email',
      headerName: 'Email',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: 'has_geo_position',
      headerName: 'Ubicación',
      flex: 0.8,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => renderBooleanCell(params.value),
    },
    {
      field: 'enabled',
      headerName: 'ESTADO',
      flex: 1.2,
      minWidth: 150,
      filterable: true,
      filterOperators: [
        {
          label: 'Igual a',
          value: 'is',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.field || !filterItem.value) {
              return null;
            }
            return (params) => {
              return String(params.value) === String(filterItem.value);
            };
          },
          InputComponent: (props: { item: any; applyValue: (value: any) => void }) => {
            const handleChange = (event: SelectChangeEvent) => {
              props.applyValue({ ...props.item, value: event.target.value });
            };
            
            return (
              <Select
                value={props.item.value || ''}
                onChange={handleChange}
                displayEmpty
                size="small"
                fullWidth
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            );
          },
        },
      ],
      renderCell: (params) => {
        const isActive = params.row.enabled;
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={isActive}
                onChange={(e) => handleStatusChange(params.row.id, e.target.checked)}
                color={isActive ? 'success' : 'default'}
                size="small"
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isActive ? 'success.main' : 'text.secondary',
                  fontWeight: 500,
                  minWidth: 70,
                  textAlign: 'center'
                }}
              >
                {isActive ? 'Activo' : 'Inactivo'}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      sortable: false,
      width: 120,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleViewDetails(params.row.id)}
          size="small"
          sx={{
            color: 'rgba(0, 0, 0, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              color: 'rgba(0, 0, 0, 0.9)',
            },
          }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 2, // Reduced top margin
        mb: 4,
        '& .header-bold': {
          fontWeight: 'bold !important',
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: '#f8f9fa',
          borderRadius: '4px 4px 0 0',
        },
        '& .MuiDataGrid-cell': {
          padding: '4px 16px',
          display: 'flex',
          alignItems: 'center',
        },
      }}
    >
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        sx={{
          padding: '12px 16px',
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            fontSize: '1.75rem',
          }}
        >
          Gestión de Clientes
        </Typography>
        <Tooltip title="Exportar a Excel">
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon sx={{ color: '#C8102E' }} />}
            onClick={handleExportToExcel}
            sx={{
              color: '#C8102E',
              backgroundColor: 'white',
              borderColor: '#C8102E',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#C8102E',
                color: 'white',
                borderColor: '#C8102E',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
              minWidth: '180px',
              height: '40px',
              borderRadius: '4px',
            }}
          >
            Exportar a Excel
          </Button>
        </Tooltip>
      </Box>

      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Box 
          display="flex" 
          alignItems="center" 
          mb={3}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: 1,
              },
            },
          }}
        >
          <TextField
            placeholder="Buscar por nombre, canal, GEC..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <Tooltip title="Limpiar búsqueda">
                    <IconButton 
                      size="small" 
                      onClick={() => setSearchText('')}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
              },
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ 
          height: 600, 
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row': {
              minHeight: '40px !important',
              maxHeight: '40px !important',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              minHeight: '40px !important',
              maxHeight: '40px !important',
            },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
              padding: '0 16px',
              minHeight: '40px !important',
              maxHeight: '40px !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600',
              whiteSpace: 'normal',
              lineHeight: '1.2',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
          },
        }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pageSizeOptions={[20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-cell': {
                padding: '8px 16px',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.07) !important',
                  cursor: 'pointer',
                },
              },
              '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.15) !important',
              },
            }}
            localeText={{
              noRowsLabel: 'No hay clientes para mostrar',
              footerRowSelected: (count) =>
                count !== 1
                  ? `${count.toLocaleString()} filas seleccionadas`
                  : '1 fila seleccionada',
              filterPanelAddFilter: 'Agregar filtro',
              filterPanelRemoveAll: 'Eliminar todos',
              filterPanelDeleteIconLabel: 'Eliminar',
              filterPanelLogicOperator: 'Operador lógico',
              filterPanelOperator: 'Operador',
              filterPanelOperatorAnd: 'Y',
              filterPanelOperatorOr: 'O',
              filterPanelColumns: 'Columnas',
              filterPanelInputLabel: 'Valor',
              filterPanelInputPlaceholder: 'Valor del filtro',
              filterOperatorContains: 'contiene',
              filterOperatorEquals: 'es igual a',
              filterOperatorStartsWith: 'comienza con',
              filterOperatorEndsWith: 'termina con',
              filterOperatorIs: 'es',
              filterOperatorNot: 'no es',
              filterOperatorAfter: 'es posterior a',
              filterOperatorOnOrAfter: 'es en o posterior a',
              filterOperatorBefore: 'es anterior a',
              filterOperatorOnOrBefore: 'es en o anterior a',
              filterOperatorIsEmpty: 'está vacío',
              filterOperatorIsNotEmpty: 'no está vacío',
              filterOperatorIsAnyOf: 'es cualquiera de',
              filterValueAny: 'cualquiera',
              filterValueTrue: 'sí',
              filterValueFalse: 'no',
            }}
          />
        </Box>
      </Paper>

      {/* Client Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalles del Cliente</DialogTitle>
        <DialogContent dividers>
          {selectedClient ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
                    {selectedClient.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ID FEMSA: {selectedClient.id}
                  </Typography>
                </Box>
                <Chip 
                  label={selectedClient.enabled ? 'Habilitado' : 'Deshabilitado'} 
                  color={selectedClient.enabled ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                <div style={{ flex: '1', minWidth: '300px' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Información del Negocio
                  </Typography>
                  <Box pl={4} mb={3}>
                    <Typography variant="body1">
                      <strong>Razón Social:</strong> {selectedClient.legal_name || 'No disponible'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>CUIT:</strong> {selectedClient.cuit || 'No disponible'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Canal:</strong> {selectedClient.channel || 'No disponible'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>GEC:</strong> {selectedClient.gec || 'No disponible'}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Ubicación
                  </Typography>
                  <Box pl={4} mb={3}>
                    <Typography variant="body1">
                      {selectedClient.street_name} {selectedClient.street_number}
                    </Typography>
                    {selectedClient.cross_street_1 && selectedClient.cross_street_2 && (
                      <Typography variant="body2" color="textSecondary">
                        Entre {selectedClient.cross_street_1} y {selectedClient.cross_street_2}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {selectedClient.locality}, {selectedClient.municipality}
                    </Typography>
                    <Typography variant="body2">
                      {selectedClient.region_description}
                    </Typography>
                    {selectedClient.latitude && selectedClient.longitude && (
                      <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                        <LocationIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
                        {` ${selectedClient.latitude}, ${selectedClient.longitude}`}
                      </Typography>
                    )}
                  </Box>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <ChatIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    Contacto
                  </Typography>
                  <Box pl={4} mb={3}>
                    {isEditing ? (
                      <>
                        <Box mb={2}>
                          <TextField
                            fullWidth
                            label="Número de móvil"
                            name="mobile_number"
                            value={editingClient?.mobile_number || ''}
                            onChange={handleInputChange}
                            size="small"
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MobileIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Teléfono fijo"
                            name="phone_number"
                            value={editingClient?.phone_number || ''}
                            onChange={handleInputChange}
                            size="small"
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PhoneIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ mb: 2 }}
                          />
                          <TextField
                            fullWidth
                            label="Correo electrónico"
                            name="email"
                            type="email"
                            value={editingClient?.email || ''}
                            onChange={handleInputChange}
                            size="small"
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                        {selectedClient.mobile_number && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <MobileIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {selectedClient.mobile_number} (Móvil)
                            </Typography>
                          </Box>
                        )}
                        {selectedClient.phone_number && (
                          <Box display="flex" alignItems="center" mb={1}>
                            <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {selectedClient.phone_number} (Teléfono)
                            </Typography>
                          </Box>
                        )}
                        {selectedClient.email ? (
                          <Box display="flex" alignItems="center" mb={1}>
                            <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1">
                              {selectedClient.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            <EmailIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            No hay correo electrónico registrado
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                    <LocationOnIcon sx={{ mr: 1 }} />
                    Ubicación en el Mapa
                  </Typography>
                  <Box pl={4}>
                    {selectedClient.latitude && selectedClient.longitude ? (
                      <Box sx={{ mt: 1 }}>
                        {mapLoaded ? (
                          <MapView 
                            lat={selectedClient.latitude} 
                            lng={selectedClient.longitude} 
                          />
                        ) : (
                          <Box 
                            sx={{ 
                              height: 300, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              border: '1px dashed #e0e0e0'
                            }}
                          >
                            <Typography color="textSecondary">Cargando mapa...</Typography>
                          </Box>
                        )}
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Coordenadas: {selectedClient.latitude}, {selectedClient.longitude}
                        </Typography>
                      </Box>
                    ) : (
                      <Box 
                        sx={{ 
                          height: 200, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          border: '1px dashed #e0e0e0'
                        }}
                      >
                        <Typography color="textSecondary">No hay datos de ubicación disponibles</Typography>
                      </Box>
                    )}
                  </Box>
                </div>
              </div>
            </Box>
          ) : (
            <Typography>Cargando detalles del cliente...</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            {isEditing ? (
              <Button 
                onClick={handleCancelEdit}
                variant="outlined"
                color="error"
                disabled={saveLoading}
                sx={{ mr: 1, '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
              >
                Cancelar
              </Button>
            ) : (
              <Button 
                onClick={handleEditClick}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{ mr: 1, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                Editar
              </Button>
            )}
          </div>
          <div>
            {isEditing ? (
              <Button 
                onClick={handleSaveChanges}
                variant="contained"
                color="primary"
                disabled={saveLoading}
                startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{ '&:hover': { backgroundColor: 'primary.dark' } }}
              >
                {saveLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            ) : (
              <Button 
                onClick={() => setDetailDialogOpen(false)}
                variant="outlined"
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                Cerrar
              </Button>
            )}
          </div>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientsPage;
